import React from 'react';
import { motion } from 'framer-motion';
import { Edit, Wand2, Trash2, Image as ImageIcon, Check, Ban, ShoppingBag, Clock, Star } from 'lucide-react';

/**
 * ArtworkList — admin artwork grid with Edit / AI Rewrite / Sold / Delete actions.
 *
 * Pure presentational: parent owns the filtered array and action handlers.
 *
 * Props:
 *  - artworks: filtered list to render (parent applies search/filter)
 *  - onEdit(artwork): open edit form
 *  - onRewrite(artwork): open AI-rewrite review modal
 *  - onDelete(id): delete by id
 *  - onToggleAvailability(artwork): flip sold/available on that row
 *  - onToggleFeatured(artwork): flip featured pin on that row
 *  - selectionMode (bool): when true, render checkbox overlays and disable
 *    individual action buttons. Clicking a card toggles selection.
 *  - selectedIds (Set<string>): currently selected ids (selection mode only)
 *  - onToggleSelect(id): callback fired when a card is toggled in selection mode
 */
const ArtworkList = ({
  artworks,
  onEdit,
  onRewrite,
  onDelete,
  onToggleAvailability,
  onToggleFeatured,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
}) => {
  if (!artworks || artworks.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500" data-testid="admin-artwork-list-empty">
        <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-20" />
        <p>No artworks found</p>
      </div>
    );
  }

  const isSelected = (id) => selectedIds?.has?.(id);

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      data-testid="admin-artwork-list"
    >
      {artworks.map((artwork) => {
        const selected = isSelected(artwork.id);
        return (
          <motion.div
            key={artwork.id}
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={selectionMode ? () => onToggleSelect?.(artwork.id) : undefined}
            className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-shadow ${
              selectionMode ? 'cursor-pointer hover:shadow-md' : 'hover:shadow-lg'
            } ${selected ? 'border-black ring-2 ring-black' : 'border-gray-200'}`}
            data-testid={`admin-artwork-card-${artwork.id}`}
          >
            <div className="relative aspect-square bg-gray-200">
              {artwork.imageUrl && (
                <img
                  src={artwork.imageUrl}
                  alt={artwork.altText || artwork.title}
                  title={artwork.title}
                  className="w-full h-full object-cover"
                />
              )}
              {selectionMode && (
                <div
                  className={`absolute top-3 left-3 w-7 h-7 rounded-md border-2 flex items-center justify-center transition-colors ${
                    selected
                      ? 'bg-black border-black text-white'
                      : 'bg-white/90 border-white'
                  }`}
                  data-testid={`admin-artwork-checkbox-${artwork.id}`}
                  aria-label={selected ? 'Selected' : 'Not selected'}
                >
                  {selected && <Check className="w-4 h-4" />}
                </div>
              )}
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-1">{artwork.artistName}</p>
              <h3 className="text-xl font-semibold mb-2">{artwork.title}</h3>
              {artwork.year && <p className="text-sm text-gray-600 mb-1">{artwork.year}</p>}
              {artwork.medium && (
                <p className="text-sm text-gray-600 mb-3">{artwork.medium}</p>
              )}
              <p className="text-lg font-medium mb-3">{artwork.priceDisplay}</p>

              <div className="flex flex-wrap gap-2 mb-3">
                {artwork.featured && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                    <Star className="w-3 h-3 fill-current" />
                    Pinned to Homepage
                  </span>
                )}
                {artwork.status === 'on_hold' && (
                  <span className="inline-block px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded">
                    On Hold
                  </span>
                )}
                {!artwork.available && artwork.status !== 'on_hold' && (
                  <span className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                    Sold
                  </span>
                )}
              </div>

              {/* On Hold reason — admin-only note, shown as a subtle amber ribbon */}
              {artwork.status === 'on_hold' && artwork.on_hold_reason && (
                <p
                  className="mb-3 px-3 py-2 bg-amber-50 border-l-2 border-amber-300 text-xs text-amber-900 italic"
                  data-testid={`admin-artwork-on-hold-reason-${artwork.id}`}
                >
                  {artwork.on_hold_reason}
                </p>
              )}

              {!selectionMode && (
                <div className="flex gap-2">
                  <button
                    onClick={() => onEdit(artwork)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
                    data-testid={`admin-artwork-edit-${artwork.id}`}
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => onToggleFeatured?.(artwork)}
                    title={artwork.featured ? 'Unpin from Homepage "Latest"' : 'Pin to Homepage "Latest"'}
                    aria-label="Toggle homepage featured pin"
                    className={`px-4 py-2 border rounded-lg flex items-center justify-center transition-colors ${
                      artwork.featured
                        ? 'border-yellow-400 text-yellow-700 bg-yellow-50 hover:bg-yellow-100'
                        : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                    data-testid={`admin-artwork-featured-${artwork.id}`}
                  >
                    <Star className={`w-4 h-4 ${artwork.featured ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    onClick={() => onToggleAvailability?.(artwork)}
                    title={
                      artwork.status === 'on_hold' ? 'Currently On Hold — click to cycle to Sold'
                      : artwork.status === 'sold' || artwork.available === false
                        ? 'Currently Sold — click to cycle to Available'
                        : 'Currently Available — click to cycle to On Hold'
                    }
                    aria-label="Cycle status Available → On Hold → Sold"
                    className={`px-4 py-2 border rounded-lg flex items-center justify-center gap-2 transition-colors ${
                      artwork.status === 'on_hold'
                        ? 'border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100'
                        : artwork.available === false || artwork.status === 'sold'
                          ? 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid={`admin-artwork-availability-${artwork.id}`}
                  >
                    {artwork.status === 'on_hold' ? (
                      <Clock className="w-4 h-4" />
                    ) : artwork.available === false || artwork.status === 'sold' ? (
                      <Ban className="w-4 h-4" />
                    ) : (
                      <ShoppingBag className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => onRewrite(artwork)}
                    title="AI rewrite description"
                    data-testid={`rewrite-${artwork.id}`}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
                  >
                    <Wand2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(artwork.id)}
                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                    data-testid={`admin-artwork-delete-${artwork.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default ArtworkList;
